import { useState, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { useGrcDashboardChart } from "@/services/dashboard";
import { useFiscalYears } from "@/services/draftCalendar";
import { OccupancyForecast } from "./OccupancyForecast";
import { BarChart } from "./BarChart";
import { OccupancyAttritionChart } from "./OccupancyAttritionChart";
import { OccupancyByBusinessChart } from "./OccupancyByBusinessChart";
import { InfoTooltip } from "./InfoTooltip";
import { LabeledPieChart } from "./LabeledPieChart";
import { BusinessDropdown } from "./BusinessDropdown";
import type { PieChartModel } from "@/types/dashboard";

const DEFAULT_OTHER_COLOR = "#575757";

export function GrcDashboard() {
  const { data: fiscalYears = [] } = useFiscalYears();
  const activeFiscalYear = fiscalYears.find((fy) => fy.submissionStatus !== "Closed") ?? fiscalYears[0];
  const [selectedFyId, setSelectedFyId] = useState<number | null>(null);
  const fyId = selectedFyId ?? activeFiscalYear?.id ?? 0;

  const { data, isLoading, isError } = useGrcDashboardChart(fyId);
  const [selectedBusiness, setSelectedBusiness] = useState<string | null>(null);

  const businessColors = data?.businessColors ?? {};

  const occupancyByBusiness = useMemo<PieChartModel[]>(() => {
    if (!data?.occupancyByBusinessByPeriod) return [];
    const totals: Record<string, number> = {};
    for (const period of data.occupancyByBusinessByPeriod) {
      for (const [business, value] of Object.entries(period.values)) {
        totals[business] = (totals[business] ?? 0) + value;
      }
    }
    return Object.entries(totals)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({
        segmentName: name,
        segmentValue: value,
        segmentValueString: value.toLocaleString(),
        segmentColor: businessColors[name] ?? DEFAULT_OTHER_COLOR,
      }));
  }, [data?.occupancyByBusinessByPeriod, businessColors]);

  const allBusinessNames = useMemo(
    () => occupancyByBusiness.map((s) => s.segmentName),
    [occupancyByBusiness]
  );

  const selectedBusinessData = useMemo<PieChartModel[]>(() => {
    if (!selectedBusiness) return [];
    return occupancyByBusiness.filter((s) => s.segmentName === selectedBusiness);
  }, [selectedBusiness, occupancyByBusiness]);

  const displayPieData =
    selectedBusiness && selectedBusinessData.length > 0 ? selectedBusinessData : occupancyByBusiness;

  const onFiscalYearChange = (fyCode: string) => {
    const fy = fiscalYears.find((f) => f.code === fyCode);
    if (fy) setSelectedFyId(fy.id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand" aria-label="Loading GRC dashboard" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p role="alert" className="dashboard-error-alert">
        Failed to load GRC dashboard data. Please try refreshing the page.
      </p>
    );
  }

  return (
    <div className="pt-4">
      <div className="flex items-center mb-6">
        <section aria-label="Occupancy summary" className="occupancy-summary flex gap-8 justify-center flex-1">
          <p>
            Today&apos;s Occupancy: <strong>{Math.round(data.todayOccupancyPercentage)}%</strong>
          </p>
          <p>
            YTD Occupancy: <strong>{Math.round(data.ytdOccupancyPercentage)}%</strong>
          </p>
        </section>
        <select
          aria-label="Fiscal year"
          value={fiscalYears.find((fy) => fy.id === fyId)?.code ?? activeFiscalYear?.code ?? ""}
          onChange={(e) => onFiscalYearChange(e.target.value)}
          className="dropdown fiscal-year"
        >
          {fiscalYears.map((fy) => (
            <option key={fy.code} value={fy.code}>
              {fy.code}
            </option>
          ))}
        </select>
      </div>

      <section aria-label="DU Occupancy Forecast" className="charts-row mb-6">
        <OccupancyForecast
          fiscalYear={fiscalYears.find((fy) => fy.id === fyId)?.code ?? activeFiscalYear?.code ?? ""}
          tooltipText="Displays inventory, occupancy %, and attrition % across fiscal periods."
          periods={data.forecastPeriods.map((p) => ({
            periodNumber: p.periodNumber,
            periodLabel: p.periodLabel,
            inventory: p.inventory,
            occupancyPct: p.occupancyPct,
            attritionPct: p.attritionPct,
            bookedDays: p.bookedDays,
            actualDays: p.actualDays,
          }))}
        />
      </section>

      <section aria-label="DU Occupancy charts" className="charts-row mb-6">
        <BarChart
          title="DU Occupancy % by Meeting Type"
          tooltipText="Occupancy percentage breakdown by meeting type."
          items={data.occupancyByMeetingType}
        />
        
        <BarChart
          title="DU Occupancy % by Meeting Category"
          tooltipText="Occupancy percentage breakdown by meeting category."
          items={data.occupancyByMeetingCategory}
        />
        
      </section>

      <section aria-label="DU Occupancy by Period" className="charts-row mb-6">
        <OccupancyByBusinessChart
          tooltipText="Occupancy breakdown by sponsoring business across fiscal periods."
          items={data.occupancyByBusinessByPeriod}
          businessColors={businessColors}
        />
        <OccupancyAttritionChart
          tooltipText="Displays inventory, occupancy %, and attrition % across fiscal periods."
          items={data.occupancyAttritionByPeriod}
        />
        
      </section>

      <section aria-label="DU Occupancy by Sponsoring Business" className="charts-row mb-6">
        <div className="chart-box pie-chart-box">
          <header className="box-header">
            <div className="box-title">
              <span>DU Occupancy by Sponsoring Business</span>
              <InfoTooltip text="Room nights distribution by sponsoring business across all periods." />
            </div>
            <div className="chart-controls">
              <BusinessDropdown
                id="grc-occupancy-by-business"
                value={selectedBusiness ?? ""}
                options={[
                  { label: "All Businesses", value: "" },
                  ...allBusinessNames.map((b) => ({ label: b, value: b }))
                ]}
                onChange={(v) => setSelectedBusiness(v || null)}
                dropUp
                aria-label="Filter DU Occupancy by business"
              />
            </div>
          </header>
          <div className="pie-chart-content grc-pie-chart-content">
            <ul className="pie-legend">
              {displayPieData.map((segment) => (
                <li key={segment.segmentName} className="legend-item">
                  <span
                    className="legend-color"
                    style={{ backgroundColor: segment.segmentColor }}
                    aria-hidden="true"
                  />
                  <span>{segment.segmentName}</span>
                </li>
              ))}
            </ul>

            <div className="pie-chart-container grc-sponsoring-pie-container">
              <LabeledPieChart
                data={displayPieData}
                showLabels={false}
                selectedBusiness={selectedBusiness}
              />
            </div>
            <div className="grc-pie-chart-spacer" aria-hidden="true" />
          </div>
        </div>
      </section>

      {data.lastUpdated && (
        <div className="last-updated">
          <p>
            <strong>Last updated:</strong> {data.lastUpdated}
          </p>
        </div>
      )}
    </div>
  );
}
